import { Button, Input, Message, Modal } from "@arco-design/web-react";
import { useSendbirdStateContext } from "@sendbird/uikit-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { getButtonColor, getButtonTextColor } from "../../../../utils/cssVariables";

import "./GroupChannelSettingsDialog.css";

type Props = {
  visible: boolean;
  channelUrl: string;
  onClose: () => void;
  onUpdated: () => void;
};

export function GroupChannelSettingsDialog({
  visible,
  channelUrl,
  onClose,
  onUpdated,
}: Props) {
  const { t } = useTranslation();
  const {
    stores: {
      sdkStore: { sdk },
    },
  } = useSendbirdStateContext();
  const [groupName, setGroupName] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const loadChannel = async () => {
      if (!visible || !channelUrl) {
        return;
      }

      try {
        const channel = await sdk.groupChannel.getChannel(channelUrl);
        setGroupName(channel.name ?? "");
      } catch {
        setGroupName("");
      }
    };

    loadChannel().catch(console.error);
  }, [visible, channelUrl, sdk.groupChannel]);

  const handleSave = async () => {
    const trimmedName = groupName.trim();
    if (!trimmedName || !channelUrl) {
      return;
    }

    setIsSaving(true);
    try {
      const channel = await sdk.groupChannel.getChannel(channelUrl);
      await channel.updateChannel({ name: trimmedName });
      onUpdated();
      onClose();
      Message.success({
        content: t("messages.group_settings_success"),
        closable: true,
        duration: 3000,
      });
    } catch {
      Message.error({
        content: t("messages.group_settings_error"),
        closable: true,
        duration: 4000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      title={t("messages.group_settings_modal_title")}
      visible={visible}
      onCancel={onClose}
      className="group-channel-settings-dialog"
      footer={null}
    >
      <div className="group-channel-settings-dialog__content">
        <Input
          allowClear
          value={groupName}
          onChange={setGroupName}
          placeholder={t("messages.group_name_placeholder")}
          onPressEnter={handleSave}
        />
        <Button
          className="group-channel-settings-dialog__button"
          shape="round"
          loading={isSaving}
          disabled={!groupName.trim() || isSaving}
          style={{
            color: getButtonTextColor(),
            backgroundColor: getButtonColor(),
          }}
          onClick={handleSave}
        >
          {t("messages.group_settings_save_button")}
        </Button>
      </div>
    </Modal>
  );
}
